/**
 * How every screen prints a number.
 *
 * Five reports had written these out beside themselves — a `counts`, a `money`
 * and a `shares` `Intl.NumberFormat` each, plus a `formatRatePct` whose one
 * comment explained the same trap twice. They agreed, which is the only reason
 * nobody noticed: two tables printing a dollar amount two ways is a defect a
 * reader finds before a test does, and four copies is four chances of it. A
 * report keeps only what is its own — what an absent description says, what a
 * missing autopay date means — in its `-lib/<name>-table.ts`.
 *
 * **Every formatter answers a string, including for nothing at all.** A figure
 * that has not arrived prints the same em dash everywhere, because a reader
 * comparing two tiles should not have to work out whether two absences are the
 * same absence. A caller that wants no text at all for a missing value — a
 * tile's `detail`, left off rather than dashed — passes `undefined` itself.
 *
 * The `Intl.NumberFormat` instances are module constants: constructing one is
 * the expensive half of formatting, and a table formats one per cell.
 *
 * No React in here — `src/AGENTS.md` keeps `src/lib/` renderable-value only.
 */

/** What a figure that has not arrived prints as. One em dash, everywhere. */
export const MISSING_FIGURE = '—'

const counts = new Intl.NumberFormat('en-US')
const money = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
})
const wholeMoney = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
})
const shares = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 2
})

/**
 * Whether a figure arrived at all.
 *
 * `null` as well as `undefined`: a report answers a count it did not measure
 * with a JSON null, and a cell rendering "null" is the same defect as one
 * rendering "undefined".
 *
 * @param value - The figure, or what stands in for its absence.
 * @returns True when there is a number to print.
 */
function isFigure(value: number | null | undefined): value is number {
  return value !== null && value !== undefined && Number.isFinite(value)
}

/**
 * A count, grouped by thousands.
 *
 * @param value - The count, or nothing while it is on its way.
 * @returns The formatted count, or an em dash.
 */
export function formatCount(value: number | null | undefined): string {
  return isFigure(value) ? counts.format(value) : MISSING_FIGURE
}

/**
 * An amount in dollars, to the cent.
 *
 * @param value - The amount, in dollars, or nothing while it is on its way.
 * @returns The formatted amount, or an em dash.
 */
export function formatMoney(value: number | null | undefined): string {
  return isFigure(value) ? money.format(value) : MISSING_FIGURE
}

/**
 * An amount in dollars, rounded to the dollar.
 *
 * For a figure read by scanning a column rather than by reconciling it: a
 * returns table is read to find the code that moved, and thirteen cents of
 * precision per row costs the width that comparison needs. A reader who is
 * adding the amounts up downloads the CSV, which carries them unrounded.
 *
 * @param value - The amount, in dollars, or nothing while it is on its way.
 * @returns The formatted amount, or an em dash.
 */
export function formatWholeMoney(value: number | null | undefined): string {
  return isFigure(value) ? wholeMoney.format(value) : MISSING_FIGURE
}

/**
 * A share, as a percentage of the whole.
 *
 * The input is a fraction between 0 and 1, which is what every endpoint here
 * answers a share with. A figure the API already expressed as a percentage
 * goes to `formatRatePct` instead — this one multiplies by 100.
 *
 * @param value - The share, between 0 and 1, or nothing while it is on its way.
 * @returns The formatted share, or an em dash.
 */
export function formatShare(value: number | null | undefined): string {
  return isFigure(value) ? shares.format(value) : MISSING_FIGURE
}

/**
 * A rate the API already expressed as a percentage.
 *
 * `Intl` with `style: 'percent'` multiplies by 100, so a value that is already
 * a percentage would print a hundred times too large — `8.5223` as `852.23%`.
 * The sign is appended here instead, which is why this is not `formatShare`
 * with different options.
 *
 * @param value - The rate, as a percentage, or nothing while it is on its way.
 * @returns The rate with its sign, to two decimal places, or an em dash.
 */
export function formatRatePct(value: number | null | undefined): string {
  return isFigure(value) ? `${value.toFixed(2)}%` : MISSING_FIGURE
}

/**
 * A two-state column, in words.
 *
 * Words, not a tick: a tick in a column headed "Enabled" is read as decoration
 * by a screen reader and as a checkbox by everyone else. It is also what a
 * browser-side filter matches, which is why a reader can type `Yes`.
 *
 * @param value - The flag, or nothing while it is on its way.
 * @returns "Yes", "No", or an em dash.
 */
export function formatYesNo(value: boolean | null | undefined): string {
  if (value === null || value === undefined) {
    return MISSING_FIGURE
  }

  return value ? 'Yes' : 'No'
}
